import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Alert, Text, View } from "react-native";
import { useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import { ApiRequestError } from "@src/packages/api/api-error";
import { toErrorMessage } from "@src/packages/api/toErrorMessage";
import {
  useActiveCycle,
  useCreateCycle,
  type Cycle,
  type CycleType as ApiCycleType,
} from "@src/packages/cycles/hooks/useCycles";
import {
  Dialog,
  DialogPrimaryButton,
  SoftSecondaryButton,
} from "@louise/moon-ui";
import { EMBER } from "../onboarding/theme";
import { useReserveSpecimensForCycle } from "@src/packages/care/cryovault/useSpecimens";
import { frozenSpecimenTreatmentMatches } from "@src/packages/care/cryovault/frozenSpecimenTreatmentTarget";
import { useTranslations } from "@src/packages/i18n";
import { toLocalDateString } from "@src/packages/utils/date";
import { buildCycleContextSnapshot } from "@src/packages/experience/buildCycleContextSnapshot";
import { experienceContextQueryKey } from "@src/packages/experience/hooks/useExperienceContext";
import { useAuthorizedExperienceProjection } from "@src/packages/experience/hooks/useAuthorizedExperienceProjection";
import {
  MoonStartTreatmentModal,
  type CycleType as StartTreatmentCycleType,
  type FrozenPrefill,
  type StartTreatmentFormData,
} from "./screens/care/modals/MoonStartTreatmentModal";
import {
  createClientRequestId,
  useAcceptEmbryoFreezingPlan,
  useCreateEmbryoFreezingPlan,
} from "@src/packages/embryo-freezing-plans/hooks/useEmbryoFreezingPlans";
import { createMinimalEmbryoFreezingPlanInput } from "@src/packages/embryo-freezing-plans/minimalStartPlan";

type CreatedCycle = Partial<Cycle> & { id?: string };

type StartTreatmentRequest = {
  goal?: "preserve" | "pregnancy";
  gender?: "male" | "female" | null;
  presetCycleType?: StartTreatmentCycleType | null;
  frozenPrefill?: FrozenPrefill | null;
  source?: string;
  onCreated?: (cycle: CreatedCycle | undefined) => void;
  /** Called when the user has an open cycle and chooses to open it instead of starting a new one. */
  onOpenActiveCycle?: (cycle: Cycle) => void;
};

type PendingSpecimenReservation = {
  cycleId: string;
  selection: FrozenPrefill;
  createdCycle: CreatedCycle;
};

type StartTreatmentControllerValue = {
  openStartTreatment: (request?: StartTreatmentRequest) => void;
  closeStartTreatment: () => void;
  isStartingTreatment: boolean;
};

const StartTreatmentControllerContext =
  createContext<StartTreatmentControllerValue | null>(null);

function unwrapCreatedCycle(result: unknown): CreatedCycle | undefined {
  const raw = (result as { data?: unknown } | null | undefined)?.data;
  const nested =
    raw && typeof raw === "object" && "data" in raw
      ? (raw as { data?: unknown }).data
      : raw;

  if (!nested || typeof nested !== "object") return undefined;
  return nested as CreatedCycle;
}

const API_CYCLE_TYPES = new Set<ApiCycleType>([
  "ivf",
  "iui",
  "fet",
  "icsi",
  "oi",
  "egg_freezing",
  "embryo_freezing",
  "sperm_freezing",
]);

function isApiCycleType(value: StartTreatmentCycleType): value is ApiCycleType {
  return API_CYCLE_TYPES.has(value);
}

export function StartTreatmentControllerProvider({
  children,
}: {
  children: ReactNode;
}) {
  const t = useTranslations();
  const queryClient = useQueryClient();
  const { tenantId, patientId } = useApiClient();
  const authorizedExperience = useAuthorizedExperienceProjection();
  const createCycle = useCreateCycle();
  const createEmbryoFreezingPlan = useCreateEmbryoFreezingPlan();
  const acceptEmbryoFreezingPlan = useAcceptEmbryoFreezingPlan();
  const reserveSpecimens = useReserveSpecimensForCycle();
  const [request, setRequest] = useState<StartTreatmentRequest | null>(null);
  const [blockedByActiveCycle, setBlockedByActiveCycle] =
    useState<StartTreatmentRequest | null>(null);
  const [pendingSpecimenReservation, setPendingSpecimenReservation] =
    useState<PendingSpecimenReservation | null>(null);
  const activeCycleQuery = useActiveCycle();

  /**
   * Set when the patient closed the modal while a write was still in flight, so
   * the failure that write reports later is not announced to someone who has
   * already walked away from it.
   */
  const dismissedWhileWritingRef = useRef(false);

  /**
   * Closes even while a write is in flight.
   *
   * Refusing to close was meant to protect a cycle mid-creation, but it made an
   * unanswered request inescapable: the spinner ran on, the close button and the
   * backdrop were both refused, and only a page reload got the patient out. A
   * request that cannot be waited out has to be leavable. The write is not
   * cancelled — it continues, and the cycle it creates still lands.
   */
  const closeStartTreatment = useCallback(() => {
    if (
      createCycle.isPending ||
      createEmbryoFreezingPlan.isPending ||
      acceptEmbryoFreezingPlan.isPending ||
      reserveSpecimens.isPending
    ) {
      dismissedWhileWritingRef.current = true;
    }
    setPendingSpecimenReservation(null);
    setRequest(null);
  }, [
    acceptEmbryoFreezingPlan.isPending,
    createCycle.isPending,
    createEmbryoFreezingPlan.isPending,
    reserveSpecimens.isPending,
  ]);

  const openStartTreatment = useCallback(
    (nextRequest: StartTreatmentRequest = {}) => {
      dismissedWhileWritingRef.current = false;
      setPendingSpecimenReservation(null);
      const normalized: StartTreatmentRequest = {
        goal: nextRequest.goal ?? "pregnancy",
        gender: nextRequest.gender ?? null,
        presetCycleType: nextRequest.presetCycleType ?? null,
        frozenPrefill: nextRequest.frozenPrefill ?? null,
        source: nextRequest.source,
        onCreated: nextRequest.onCreated,
        onOpenActiveCycle: nextRequest.onOpenActiveCycle,
      };
      // Existing open cycle blocks a new one: guide the user to finish it first
      // instead of letting the API reject the create silently.
      const openCycle = activeCycleQuery.data;
      if (openCycle && openCycle.status !== "closed") {
        setBlockedByActiveCycle(normalized);
        return;
      }
      setRequest(normalized);
    },
    [activeCycleQuery.data],
  );

  const dismissBlockedByActiveCycle = useCallback(() => {
    setBlockedByActiveCycle(null);
  }, []);

  const handleOpenActiveCycle = useCallback(() => {
    const cycle = activeCycleQuery.data;
    const callback = blockedByActiveCycle?.onOpenActiveCycle;
    setBlockedByActiveCycle(null);
    if (cycle && callback) callback(cycle);
  }, [activeCycleQuery.data, blockedByActiveCycle]);

  const handleStartTreatment = useCallback(
    async (formData: StartTreatmentFormData) => {
      if (!isApiCycleType(formData.treatmentType)) {
        Alert.alert(
          t("care.overview.alerts.choose_treatment_title"),
          t("care.overview.alerts.choose_treatment_message"),
          [{ text: "OK" }],
        );
        return;
      }

      const selection = formData.frozenSelection ?? request?.frozenPrefill;
      const selectionMatchesTreatment =
        !selection ||
        frozenSpecimenTreatmentMatches(selection.type, formData.treatmentType);

      if (!selectionMatchesTreatment) {
        Alert.alert(
          t("care.overview.alerts.frozen_selection_mismatch_title"),
          t("care.overview.alerts.frozen_selection_mismatch_message"),
          [{ text: "OK" }],
        );
        return;
      }

      if (selection && selection.specimenIds.length > 0) {
        if (!formData.hasDecisionAndConsent) {
          Alert.alert(
            t("care.overview.alerts.frozen_specimen_consent_required_title"),
            t("care.overview.alerts.frozen_specimen_consent_required_message"),
            [{ text: "OK" }],
          );
          return;
        }
      }

      const startDateStr = formData.startDate
        ? toLocalDateString(formData.startDate)
        : null;

      try {
        let createdCycle: CreatedCycle | undefined;

        if (pendingSpecimenReservation) {
          createdCycle = pendingSpecimenReservation.createdCycle;
          try {
            await reserveSpecimens.mutateAsync({
              cycleId: pendingSpecimenReservation.cycleId,
              specimens: pendingSpecimenReservation.selection.specimenIds.map(
                (id) => ({
                  id,
                  type: pendingSpecimenReservation.selection.type,
                }),
              ),
              hasDecisionAndConsent: formData.hasDecisionAndConsent,
            });
          } catch (error) {
            console.error(
              "[StartTreatmentController] Retry failed for frozen specimen reservation:",
              error,
            );
            Alert.alert(
              t("care.overview.alerts.reserve_specimens_failed_title"),
              t("care.overview.alerts.reserve_specimens_failed_message"),
              [{ text: "OK" }],
            );
            return;
          }
          setPendingSpecimenReservation(null);
        } else {
          let cyclePlan: Awaited<
            ReturnType<typeof createEmbryoFreezingPlan.mutateAsync>
          > | null = null;
          if (formData.treatmentType === "embryo_freezing") {
            const planInput = createMinimalEmbryoFreezingPlanInput(
              createClientRequestId(),
            );
            const draftPlan =
              await createEmbryoFreezingPlan.mutateAsync(planInput);
            cyclePlan = await acceptEmbryoFreezingPlan.mutateAsync({
              planId: draftPlan.id,
              input: {
                clientRequestId: createClientRequestId(),
                expectedVersion: draftPlan.currentVersion,
              },
            });
          }

          const acceptedPlanVersion = cyclePlan?.versions.find(
            (candidate) => candidate.version === cyclePlan.currentVersion,
          );
          const materialIds =
            acceptedPlanVersion?.materials
              .filter((material) => material.allocationStatus === "selected")
              .map((material) => material.materialEntityId) ?? [];

          const acceptedTreatmentPlan =
            authorizedExperience.canonical?.acceptedTreatmentPlan;
          const cyclePlanSnapshot =
            acceptedTreatmentPlan?.status === "accepted" &&
            (formData.treatmentType === "iui" ||
              formData.treatmentType === "oi") &&
            acceptedTreatmentPlan.treatmentFamily === formData.treatmentType
              ? {
                  acceptedPlanId: acceptedTreatmentPlan.id,
                  acceptedPlanVersion: acceptedTreatmentPlan.version,
                  clientRequestId: createClientRequestId(),
                }
              : undefined;

          const createResult = await createCycle.mutateAsync({
            cycleType: formData.treatmentType,
            provider: formData.clinicName || null,
            startDate: startDateStr,
            status: "open",
            ...(authorizedExperience.canonical
              ? {
                  contextSnapshot: buildCycleContextSnapshot(
                    authorizedExperience.canonical,
                    materialIds,
                  ),
                }
              : {}),
            ...(cyclePlan
              ? {
                  embryoFreezingPlanSnapshot: {
                    planId: cyclePlan.id,
                    expectedVersion: cyclePlan.currentVersion,
                    clientRequestId: createClientRequestId(),
                  },
                }
              : {}),
            ...(cyclePlanSnapshot ? { cyclePlanSnapshot } : {}),
          });
          createdCycle = unwrapCreatedCycle(createResult);

          if (!createdCycle?.id) {
            throw new Error("Cycle creation returned no cycle id");
          }

          queryClient.setQueryData(
            ["active-cycle", tenantId, patientId],
            createdCycle,
          );
          // Also seed the cycles list. CycleDetail resolves a cycle from the
          // list by id (via useActiveCycleWithPhases); without this the new
          // cycle is missing on arrival and CycleDetail briefly renders the
          // "post-cycle" fallback based on the previous cycle's outcome
          // until the background refetch lands (~500ms flash).
          const seededCycle = createdCycle;
          queryClient.setQueryData<Cycle[]>(
            ["cycles", tenantId, patientId],
            (previous) => {
              const next = previous ? [...previous] : [];
              if (!next.some((entry) => entry.id === seededCycle.id)) {
                next.unshift(seededCycle as Cycle);
              }
              return next;
            },
          );

          if (selection && selection.specimenIds.length > 0) {
            try {
              await reserveSpecimens.mutateAsync({
                cycleId: createdCycle.id,
                specimens: selection.specimenIds.map((id) => ({
                  id,
                  type: selection.type,
                })),
                hasDecisionAndConsent: formData.hasDecisionAndConsent,
              });
            } catch (error) {
              // The cycle already exists at this point — do not strand the
              // user on the modal. Warn about the reservation failure but
              // let the flow proceed to CycleDetail so the created cycle is
              // reachable. The pending reservation is kept so future work
              // can surface a retry action from CycleDetail.
              console.error(
                "[StartTreatmentController] Failed to reserve frozen specimens:",
                error,
              );
              setPendingSpecimenReservation({
                cycleId: createdCycle.id,
                selection,
                createdCycle,
              });
              Alert.alert(
                t("care.overview.alerts.reserve_specimens_failed_title"),
                t("care.overview.alerts.reserve_specimens_failed_message"),
                [{ text: "OK" }],
              );
            }
          }
        }

        // Mark stale without awaiting refetches: on a slow dev API the six
        // sequential refetches keep the button in a loading state for tens
        // of seconds. The active-cycle cache is already populated
        // optimistically (`setQueryData` above), so downstream screens
        // render immediately and refetch in the background.
        void queryClient.invalidateQueries({ queryKey: ["active-cycle"] });
        void queryClient.invalidateQueries({ queryKey: ["cycles"] });
        void queryClient.invalidateQueries({ queryKey: ["cycle-phases"] });
        void queryClient.invalidateQueries({ queryKey: ["medications"] });
        void queryClient.invalidateQueries({ queryKey: ["specimens"] });
        void queryClient.invalidateQueries({
          queryKey: experienceContextQueryKey(patientId),
        });

        const onCreated = request?.onCreated;
        setRequest(null);
        // Defer navigation so the modal has committed unmount before the
        // route changes. Without this, on web the Modal overlay can linger
        // over the new screen and the user stays visually on CryoVault.
        if (onCreated) {
          setTimeout(() => onCreated(createdCycle), 0);
        }
      } catch (error) {
        // Log the normalized status and message, not just the object. The
        // patient-facing alert stays deliberately generic, so this line is the
        // only place the actual reason is observable.
        const status =
          error instanceof ApiRequestError && error.status !== undefined
            ? ` (HTTP ${error.status})`
            : "";
        console.error(
          `[StartTreatmentController] Failed to start cycle${
            request?.source ? ` from ${request.source}` : ""
          }${status}: ${toErrorMessage(error)}`,
          error,
        );
        // Still logged above, but not announced: the patient closed this while
        // it was in flight, and an alert about a screen they have already left
        // interrupts whatever they moved on to.
        if (!dismissedWhileWritingRef.current) {
          Alert.alert(
            t("care.overview.alerts.unable_to_start_title"),
            t("care.overview.alerts.unable_to_start_message"),
            [{ text: "OK" }],
          );
        }
      } finally {
        dismissedWhileWritingRef.current = false;
      }
    },
    [
      createCycle,
      createEmbryoFreezingPlan,
      acceptEmbryoFreezingPlan,
      patientId,
      queryClient,
      request,
      reserveSpecimens,
      t,
      tenantId,
      pendingSpecimenReservation,
      authorizedExperience,
    ],
  );

  const value = useMemo(
    () => ({
      openStartTreatment,
      closeStartTreatment,
      isStartingTreatment:
        createCycle.isPending ||
        createEmbryoFreezingPlan.isPending ||
        acceptEmbryoFreezingPlan.isPending ||
        reserveSpecimens.isPending,
    }),
    [
      closeStartTreatment,
      acceptEmbryoFreezingPlan.isPending,
      createCycle.isPending,
      createEmbryoFreezingPlan.isPending,
      openStartTreatment,
      reserveSpecimens.isPending,
    ],
  );

  return (
    <StartTreatmentControllerContext.Provider value={value}>
      {children}
      <MoonStartTreatmentModal
        visible={request !== null}
        onClose={closeStartTreatment}
        onStartTreatment={handleStartTreatment}
        isLoading={
          createCycle.isPending ||
          createEmbryoFreezingPlan.isPending ||
          acceptEmbryoFreezingPlan.isPending ||
          reserveSpecimens.isPending
        }
        goal={request?.goal ?? "pregnancy"}
        gender={request?.gender ?? null}
        presetCycleType={request?.presetCycleType ?? null}
        frozenPrefill={request?.frozenPrefill ?? null}
        frozenSelectionCycleId={pendingSpecimenReservation?.cycleId ?? null}
      />
      <Dialog
        visible={blockedByActiveCycle !== null}
        onClose={dismissBlockedByActiveCycle}
        layout="centered"
        closeButton="minimal"
        containerStyle={{ maxWidth: 420 }}
        testID="cycle-already-open-dialog"
      >
        <View style={{ alignItems: "center", marginBottom: 18 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 18,
              fontWeight: "600",
              color: EMBER.colors.text,
              marginBottom: 8,
              textAlign: "center",
            }}
          >
            {t("care.start_treatment.cycle_already_open.you_have_open_cycle")}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              color: EMBER.colors.textLight,
              lineHeight: 18,
              textAlign: "center",
            }}
          >
            {t(
              "care.start_treatment.cycle_already_open.finish_before_starting_new",
            )}
          </Text>
        </View>
        <View style={{ flexDirection: "row", gap: 12 }}>
          <SoftSecondaryButton
            label={t("care.start_treatment.cycle_already_open.not_now")}
            onPress={dismissBlockedByActiveCycle}
            flex={1}
          />
          <DialogPrimaryButton
            label={t(
              "care.start_treatment.cycle_already_open.go_to_current_cycle",
            )}
            onPress={handleOpenActiveCycle}
            disabled={!blockedByActiveCycle?.onOpenActiveCycle}
            flex={1.1}
          />
        </View>
      </Dialog>
    </StartTreatmentControllerContext.Provider>
  );
}

export function useStartTreatmentController() {
  const context = useContext(StartTreatmentControllerContext);
  if (!context) {
    throw new Error(
      "useStartTreatmentController must be used inside StartTreatmentControllerProvider",
    );
  }
  return context;
}
